import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, AlertTriangle, MoreHorizontal } from "lucide-react";
import { InstagramIcon } from "./BrandIcons";
import ReconnectNeededBadge from "./ReconnectNeededBadge";
import { channelHealthKey, useChannelHealth } from "@/hooks/useChannelHealth";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edgeError";
import { launchInstagramOAuth, instagramOAuthConfigured } from "@/lib/instagramOAuth";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

// Instagram DM connection card.
//
// Phase 1 (now): manual paste-in. The owner pastes a long-lived Instagram
// access token + their IG Business Account ID. We validate via a live /me
// probe (instagram-onboard → connector.healthCheck) and persist. A future
// "Connect with Facebook" OAuth button replaces this once Meta App Review
// clears the messaging permissions.

interface InstagramState {
  connected: boolean;
  status: string;
  status_reason: string | null;
  username: string | null;
  ig_business_account_id: string | null;
  connected_at: string | null;
  /** Comment webhooks are live ("Reply to comments with a DM"). */
  comments_ready?: boolean;
}

const InstagramConnectionCard = ({ className }: { className?: string }) => {
  const { user, businessId } = useAuth();
  const queryClient = useQueryClient();

  const [setupOpen, setSetupOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [accountIdInput, setAccountIdInput] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const oauthAvailable = instagramOAuthConfigured();

  const { data: state, isLoading } = useQuery({
    queryKey: ["instagramConnection", user?.id],
    queryFn: async (): Promise<InstagramState | null> => {
      if (!user) return null;
      const { data, error } = await supabase.functions.invoke("instagram-onboard", {
        method: "GET",
      });
      if (error) throw error;
      return (data as InstagramState) || null;
    },
    enabled: !!user,
    staleTime: 15000,
  });

  const isConnected = !!state?.connected;
  const username = state?.username;
  // Meta rejected the token: a failed reply records it at once, and the
  // daily probe also sets the connection's status to 'error'.
  const { authFailed } = useChannelHealth("instagram");
  const needsReconnect = (isConnected && authFailed) || state?.status === "error";
  const showConnect = !isConnected || needsReconnect;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["instagramConnection", user?.id] }),
      queryClient.invalidateQueries({ queryKey: channelHealthKey(businessId, "instagram") }),
    ]);

  const handleConnect = async () => {
    const accessToken = tokenInput.trim();
    const igBusinessAccountId = accountIdInput.trim();
    if (!accessToken || !igBusinessAccountId) {
      toast.error("Paste both your Instagram access token and your IG Business Account ID.");
      return;
    }
    setConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("instagram-onboard", {
        body: {
          action: "connect_manual",
          accessToken,
          igBusinessAccountId,
          igUsername: usernameInput.trim() || undefined,
        },
      });
      if (error) {
        // supabase-js hides the function's response body on non-2xx. Dig it
        // out of error.context so the user sees Meta's actual reason.
        let detail = error.message;
        try {
          const body = await (error as any).context?.json?.();
          if (body) detail = body.detail || body.error || detail;
        } catch {
          /* keep generic message */
        }
        throw new Error(detail);
      }
      if (data?.error) throw new Error(data.detail || data.error);
      toast.success("Instagram connected. DMs to your account will now get AI replies.");
      setTokenInput("");
      setAccountIdInput("");
      setUsernameInput("");
      setSetupOpen(false);
      await refresh();
    } catch (err: any) {
      console.error("Instagram connect failed:", err);
      toast.error(err?.message || "Couldn't connect Instagram.");
    } finally {
      setConnecting(false);
    }
  };

  const handleConnectOAuth = async () => {
    setOauthConnecting(true);
    try {
      // Keep the comments permission on a reconnect if it was granted before;
      // the workflow panel is where an owner first opts into it.
      const { code, redirectUri } = await launchInstagramOAuth({
        comments: !!state?.comments_ready,
      });
      const { data, error } = await supabase.functions.invoke("instagram-onboard", {
        body: { action: "connect_oauth", code, redirectUri },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.detail || data.error);
      toast.success("Instagram connected. DMs to your account will now get AI replies.");
      setSetupOpen(false);
      await refresh();
    } catch (err: any) {
      console.error("Instagram OAuth connect failed:", err);
      const msg = err?.message?.includes("cancelled") || err?.message?.includes("Popup")
        ? err.message
        : await readEdgeError(err, "Couldn't connect Instagram.");
      toast.error(msg);
    } finally {
      setOauthConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("instagram-onboard", {
        body: { action: "disconnect" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Instagram disconnected.");
      await refresh();
      setDisconnectOpen(false);
    } catch (err: any) {
      console.error("Instagram disconnect failed:", err);
      toast.error(err?.message || "Couldn't disconnect.");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      <div className={`flex items-center justify-between gap-4 p-4 border rounded-xl bg-card shadow-sm ${className ?? ""}`}>
        <div className="flex items-center gap-4 min-w-0">
          <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#E4405F]/10 shrink-0">
            <InstagramIcon className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm">Instagram</h3>
              {needsReconnect && <ReconnectNeededBadge />}
              {isConnected && !needsReconnect && (
                <Badge
                  variant="default"
                  className="bg-green-600 hover:bg-green-600 text-white border-transparent"
                >
                  Connected
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {needsReconnect
                ? "Meta stopped accepting Dibs's connection, so DMs aren't getting replies. Reconnect to fix it."
                : isConnected
                ? "Dibs replies to DMs sent to your Instagram account."
                : "Connect your Instagram account so Dibs can reply to DMs."}
            </p>
          </div>
        </div>
        <div className="shrink-0">
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <div className="flex items-center gap-3">
              {needsReconnect && (
                <Button size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
                  Reconnect
                </Button>
              )}
              <Switch
                checked={isConnected}
                disabled={connecting || disconnecting}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSetupOpen(true);
                  } else {
                    setDisconnectOpen(true);
                  }
                }}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted">
                    <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isConnected && (
                    <>
                      <DropdownMenuLabel className="font-normal text-muted-foreground">
                        {username ? `Connected as @${username}` : "Connected"}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => setSetupOpen(true)}>
                    {isConnected ? "Manage connection" : "Connect Instagram"}
                  </DropdownMenuItem>
                  {isConnected && (
                    <DropdownMenuItem
                      onClick={() => setDisconnectOpen(true)}
                      className="text-destructive"
                    >
                      Disconnect
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Setup / manage modal */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="admin-modal-content sm:max-w-xl">
          <DialogHeader className="admin-modal-header">
            <DialogTitle>
              {needsReconnect ? "Reconnect Instagram" : isConnected ? "Instagram connection" : "Connect Instagram"}
            </DialogTitle>
            <DialogDescription>
              {needsReconnect
                ? "Meta stopped accepting Dibs's connection. Connect again to restore replies; your conversations stay in the inbox."
                : isConnected
                ? "Your Instagram channel is live. DMs appear in your unified inbox and your AI replies on your behalf."
                : "Paste your Instagram access token and Business Account ID to connect. One-click connect with Facebook is coming soon."}
            </DialogDescription>
          </DialogHeader>

          {showConnect && oauthAvailable && (
            <div className="space-y-4">
              <Button onClick={handleConnectOAuth} disabled={oauthConnecting} className="w-full">
                {oauthConnecting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Connect with Instagram
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                You'll be asked to log in to Instagram and authorize Dibs. No tokens to copy.
              </p>
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or connect manually</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
          )}

          {showConnect && (
            <div className="space-y-5">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Where to get these</AlertTitle>
                <AlertDescription>
                  <ol className="list-decimal pl-5 mt-2 space-y-1 text-sm">
                    <li>Your Instagram account must be a Professional (Business/Creator) account.</li>
                    <li>
                      In your Meta App, generate a long-lived Instagram access token with messaging
                      permissions and copy your Instagram Business Account ID.
                    </li>
                    <li>Paste both below. They're stored encrypted.</li>
                  </ol>
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="ig-account-id">Instagram Business Account ID</Label>
                <Input
                  id="ig-account-id"
                  placeholder="17841400000000000"
                  value={accountIdInput}
                  onChange={(e) => setAccountIdInput(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ig-token">Access token</Label>
                <Input
                  id="ig-token"
                  type="password"
                  placeholder="IGQVJ..."
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Stored encrypted. Used to send & receive Instagram DMs on your behalf.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ig-username">Username (optional)</Label>
                <Input
                  id="ig-username"
                  placeholder="your_handle"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {isConnected && (
            <div className="space-y-2 text-sm">
              {username && (
                <div>
                  <span className="text-muted-foreground">Account: </span>@{username}
                </div>
              )}
              {state?.ig_business_account_id && (
                <div className="text-xs text-muted-foreground font-mono">
                  ID: {state.ig_business_account_id}
                </div>
              )}
              {state?.connected_at && (
                <div className="text-xs text-muted-foreground">
                  Connected {new Date(state.connected_at).toLocaleDateString()}.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="standard-action-button admin-modal-secondary-button neutral-action-button"
              onClick={() => setSetupOpen(false)}
            >
              Close
            </Button>
            {showConnect && (
              <Button
                size="sm"
                className="standard-action-button"
                onClick={handleConnect}
                disabled={connecting || !tokenInput.trim() || !accountIdInput.trim()}
              >
                {connecting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Connect Instagram
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect confirmation */}
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Instagram?</AlertDialogTitle>
            <AlertDialogDescription>
              Your AI will stop responding to Instagram DMs. Existing conversations stay in your
              inbox. You can reconnect any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={disconnecting}
              onClick={(e) => {
                e.preventDefault();
                handleDisconnect();
              }}
            >
              {disconnecting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default InstagramConnectionCard;
